import type { GarageProperty } from "@App/apis/graphql/perimeterSearch/responses/garageProperty";
import { IconLabel } from "@App/Components/atoms/common/IconLabel";
import { CircleDashed } from "@App/Components/atoms/icons/CircleDashed";
import { CircleFull } from "@App/Components/atoms/icons/CircleFull";
import { CircleHalfStroke } from "@App/Components/atoms/icons/CircleHalfStroke";
import { CallbackRequestButton } from "@App/Components/CallbackRequestButton";
import { CheckpointDetails } from "@App/Components/CheckpointDetails";
import { Alert } from "@App/Components/common/Alert";
import { CheckIcon } from "@App/Components/common/Icons";
import { DayPicker } from "@App/Components/DayPicker/DayPicker";
import { BookingHeadline } from "@App/Components/Partials/BookingHeadline";
import { SelectedHint } from "@App/Components/Partials/SelectedHint";
import { PremiumServiceSelector } from "@App/Components/PremiumServiceSelector";
import { TimeSlotPicker } from "@App/Components/TimeSlotPicker";
import { GaragePropertyContext } from "@App/contexts/listing/GaragePropertyContext";
import { useNearestSlotGoneAlertTracking } from "@App/hooks/analytics/booking/useNearestSlotGoneAlertTracking";
import { useNoSlotsAlertTracking } from "@App/hooks/analytics/booking/useNoSlotsAlertTracking";
import { useBlockSlot } from "@App/hooks/api/mutations/useBlockSlot";
import { useDays } from "@App/hooks/api/useDays";
import { usePremiumServices } from "@App/hooks/api/usePremiumServices";
import { useConfig } from "@App/hooks/useConfig";
import { useCountry } from "@App/hooks/useCountry";
import { useUnblockSlotOnMount } from "@App/hooks/useUnblockSlotOnMount";
import { actionCreators, type State } from "@App/state";
import type { Slot } from "@App/types";
import { safeFilter, safeMap } from "@App/utils/array";
import { OrganizationIsSignalIdunaOrItzehoerAndServiceIsGlass } from "@App/utils/conditionals/combinations";
import {
  OrganizationIsALD,
  OrganizationIsArval,
  OrganizationIsMFS,
  OrganizationIsMobilityConcept,
} from "@App/utils/conditionals/organization";
import { ServiceIsVehicleReturn } from "@App/utils/conditionals/services";
import { formatDate } from "@App/utils/dates";
import { eligibleGarageProperties } from "@App/utils/garages/eligibleGaragePropertiesUtil";
import { getConfig } from "@App/utils/getConfig";
import { safeHtmlToReact } from "@App/utils/safeHtmlToReact";
import { bindActionCreators } from "@reduxjs/toolkit";
import {
  addBusinessDays,
  addDays,
  addMonths,
  differenceInMonths,
  endOfMonth,
  isAfter,
  isToday,
  max,
  min,
  parseISO,
  startOfDay,
  startOfMonth,
  subBusinessDays,
  subWeeks,
} from "date-fns";
import { Fragment, type FunctionComponent, useContext, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Navigate } from "react-router-dom";

type BookingProps = {
  onSubmit: (slot: Slot) => void;
};

export const Booking: FunctionComponent<BookingProps> = ({ onSubmit }: BookingProps) => {
  const { availableGarageProperties } = useContext(GaragePropertyContext);

  // hooks
  const { t } = useTranslation();
  // unblock a blocked slot, if the user already clicked and blocked a
  // slot in this session
  useUnblockSlotOnMount();
  // block slot graphql api hook for slot reservation
  const {
    triedSlot,
    blockSlot,
    error: errorWhenBlockingSlot,
    loading: loadingWhenBlockingSlot,
    blockedSlot,
  } = useBlockSlot();

  // redux selectors
  const { displayPremiumServices } = useConfig();
  const service = useSelector((state: State) => state.orders.service);
  const garage = useSelector((state: State) => state.orders.garage);
  const selectedSlot = useSelector((state: State) => state.orders.slot);
  const organization = useSelector((state: State) => state.orders.organization);
  const brand = useSelector((state: State) => state.orders.brand);
  const selectedPremiumServices = useSelector((state: State) => state.orders.premiumServices);
  const fleetVehicleReturn = useSelector((state: State) => state.fleet.vehicleReturn);
  const countryCode = useCountry();
  const config = getConfig(organization);

  // redux action creators
  const dispatch = useDispatch();
  const { chooseSlot } = bindActionCreators(actionCreators, dispatch);

  // component state
  const [nextSlot, setNextSlot] = useState(null);
  const [initiallySelectedDay, setInitiallySelectedDay] = useState(null);

  const [selectedDay, setSelectedDay] = useState(
    selectedSlot ? startOfDay(parseISO(selectedSlot.starts_at)) : null,
  );
  const [daysAvailable, setDaysAvailable] = useState(true);

  // business logic
  const changeDay = (day: Date) => {
    if (!initiallySelectedDay) {
      setInitiallySelectedDay(day);
    }
    setSelectedDay(day);
  };

  const changeSlot = (slot: Slot) => {
    chooseSlot(slot);
  };

  const changeEarliestSlot = (slot: Slot) => {
    setNextSlot(slot);
  };

  const selectFirstSlot = () => {
    setSelectedDay(startOfDay(parseISO(nextSlot.starts_at)));
    chooseSlot(nextSlot);
  };

  const { premiumServices: availablePremiumServices } = usePremiumServices();

  const premiumServices = useSelector((state: State) => state.orders.premiumServices);

  const noSubServiceSelected = () => {
    // Disable this for Ayvens: they do not need a subservice selected.
    if (OrganizationIsALD(organization) || OrganizationIsArval(organization)) {
      return false;
    }
    if (
      availablePremiumServices?.filter(
        (premiumService) => premiumService.service_type === "sub-service",
      ).length > 0
    ) {
      return (
        premiumServices.filter((premiumService) => premiumService.service_type === "sub-service")
          .length == 0
      );
    }
    return false;
  };

  const submit = () => {
    if (!selectedSlot) {
      return;
    }
    blockSlot(selectedSlot);
  };

  useEffect(() => {
    if (blockedSlot && blockedSlot.id) {
      chooseSlot(blockedSlot);
      onSubmit && onSubmit(selectedSlot);
    }
  }, [blockedSlot]);

  /**
   * from DayPicker
   */
  const slot = useSelector((state: State) => state.orders.slot);
  const [earliestStartDate, setEarliestDate] = useState(
    garage?.available_days?.length && garage.available_days.length > 0
      ? startOfMonth(parseISO(garage.available_days[0].available_at))
      : startOfMonth(new Date()),
  );

  const [activeStartDate, setActiveStartDate] = useState<Date>(
    slot ? parseISO(slot.starts_at) : earliestStartDate,
  );
  const [userInputReceived, setUserInputReceived] = useState<boolean>(false);

  // Look from the start of the month
  // For MFS vehicle return, clamp this to -10 bd before contract end
  // For Mobility Concept vehicle return, clamp this to -4 weeks before contract end
  const getFrom = () => {
    const monthStart = startOfMonth(activeStartDate);

    const endDate = fleetVehicleReturn ? new Date(fleetVehicleReturn.endDate) : null;
    if (OrganizationIsMFS(organization) && ServiceIsVehicleReturn(service) && endDate) {
      return max([monthStart, subBusinessDays(endDate, 10)]);
    }
    if (OrganizationIsMobilityConcept(organization) && ServiceIsVehicleReturn(service) && endDate) {
      return max([monthStart, subWeeks(endDate, 4)]);
    }
    return monthStart;
  };

  // Look until end of the month
  // For MFS vehicle return, clamp this to +10 bd after contract end,
  // or if contract end is after today, clamp to +90 days after contract end
  const getTo = () => {
    const monthEnd = endOfMonth(activeStartDate);

    const endDate = fleetVehicleReturn ? new Date(fleetVehicleReturn.endDate) : null;
    if (OrganizationIsMFS(organization) && ServiceIsVehicleReturn(service) && endDate) {
      if (isAfter(new Date(), endDate) || isToday(endDate)) {
        return min([monthEnd, addDays(endDate, 90)]);
      }

      return min([monthEnd, addBusinessDays(endDate, 10)]);
    }

    return monthEnd;
  };
  const from = getFrom();
  const to = getTo();
  const { loading, days, error } = useDays(from, to);

  useEffect(() => {
    if (nextSlot) {
      setEarliestDate(startOfMonth(parseISO(nextSlot.starts_at)));
    }
  }, [nextSlot]);

  useEffect(() => {
    if (!loading && selectedDay && activeStartDate.getMonth() !== selectedDay.getMonth())
      changeDay(days[0]);
  }, [loading, selectedDay, activeStartDate]);

  useEffect(() => {
    // select the first available day on initial load
    if (!loading && !selectedDay && days) {
      changeDay(days[0]);
    }
    // return if there are available days in the selected month
    if (days && !loading) {
      setDaysAvailable(days.length > 0);
    }
    if (
      !loading &&
      !userInputReceived &&
      days.length === 0 &&
      differenceInMonths(activeStartDate, new Date()) < 1
    ) {
      onChangeMonth({ activeStartDate: addMonths(activeStartDate, 1) });
    }
  }, [loading, days, error]);

  // if the picked date changed from an external source
  // reset the calendar to the right month
  useEffect(() => {
    if (selectedDay && days && days.length > 0 && selectedDay !== days[0])
      onChangeMonth({ activeStartDate: selectedDay });
  }, [selectedDay]);

  const onChangeMonth = (props: { activeStartDate: Date }) => {
    setActiveStartDate(props.activeStartDate);
    setUserInputReceived(true);
  };
  /**
   * END
   */

  const nearestSlotGone =
    !!nextSlot &&
    !!garage &&
    garage.available_days.length > 0 &&
    selectedDay === initiallySelectedDay &&
    startOfDay(parseISO(nextSlot.starts_at)) >
      startOfDay(parseISO(garage.available_days[0].available_at));

  // analytics: emit when the booking calendar alerts become visible
  useNoSlotsAlertTracking({
    active: !daysAvailable && !loading,
    organizationId: organization.id,
    garageId: garage.id,
    serviceId: service.id,
  });
  useNearestSlotGoneAlertTracking({
    active: nearestSlotGone,
    organizationId: organization.id,
    garageId: garage.id,
    serviceId: service.id,
    promisedSlotAt: garage?.available_days?.[0]?.available_at ?? "",
    actualSlotAt: nextSlot?.starts_at ?? "",
  });

  if (OrganizationIsMFS(organization) && !fleetVehicleReturn?.agreementNumber) {
    return <Navigate to={"/partner/mfs"} replace={true} />;
  }

  return (
    <div className="md:container mx-auto mt-10 grid md:grid-cols-2 gap-x-16 justify-between border border-neutral-100 bg-neutral-100 rounded md:shadow p-8">
      <BookingHeadline
        service={service}
        premiumServices={selectedPremiumServices}
        organization={organization}
        brand={brand}
      />
      <div className="my-5 md:my-0 row-span-5">
        <Alert
          display={!daysAvailable && !loading}
          title={
            <>
              {t("booking.no-slots")}{" "}
              {garage.phone && (
                <Trans
                  i18nKey={"booking.no-slots-contact-phone"}
                  values={{ tel: garage.phone }}
                  components={{
                    tel: <a href={`tel:${garage.phone.replace(" ", "")}`} />,
                  }}
                />
              )}
            </>
          }
        />
        {nextSlot &&
          garage &&
          garage.available_days.length > 0 &&
          selectedDay === initiallySelectedDay && (
            <Alert
              display={
                startOfDay(parseISO(nextSlot.starts_at)) >
                startOfDay(parseISO(garage.available_days[0].available_at))
              }
              title={t("booking.error-nearest-slot-gone", {
                day: formatDate(garage.available_days[0].available_at, "dd."),
                newDay: formatDate(nextSlot.starts_at, "dd."),
              })}
              duration={10000}
            />
          )}
        <Alert
          display={!!errorWhenBlockingSlot && !!triedSlot}
          title={
            <>
              {t("booking.slot-taken-1", { service: service ? service.name : "" })}
              <b>
                &nbsp;
                {formatDate(
                  triedSlot ? triedSlot.starts_at : new Date(),
                  "EEEEEE., dd.MM.yyyy 'um' HH:mm 'Uhr'",
                )}
              </b>{" "}
              {t("booking.slot-taken-2")}
            </>
          }
        />
        {nextSlot &&
          !(OrganizationIsMFS(organization) && ServiceIsVehicleReturn(service)) &&
          !(OrganizationIsMobilityConcept(organization) && ServiceIsVehicleReturn(service)) && (
            <Fragment>
              {t("booking.next-free-slot")}{" "}
              <button
                type="button"
                className={`inline-block text-sm border rounded px-3 py-1 cursor-pointer ${nextSlot === selectedSlot ? "text-white bg-primary border-primary" : "text-primary border-neutral-700"}`}
                onClick={selectFirstSlot}
              >
                {formatDate(nextSlot["starts_at"], "EEEEEE., dd.MM.yyyy 'um' HH:mm 'Uhr'")}
              </button>
            </Fragment>
          )}
        <div className="grid md:grid-cols-[3fr_2fr] gap-3 mt-5 w-full">
          <DayPicker
            config={config}
            onChange={changeDay}
            onChangeMonth={onChangeMonth}
            selectedDay={selectedDay}
            days={days}
            loading={loading}
            activeStartDate={activeStartDate}
            earliestStartDate={earliestStartDate}
          />
          <TimeSlotPicker
            day={selectedDay}
            selectedSlot={selectedSlot}
            daysLoading={loading}
            onChange={changeSlot}
            onChangeEarliestSlot={changeEarliestSlot}
          />
        </div>
        <div className="mt-3 mb-6 text-sm font-medium text-gray-700">
          <div className="flex mb-1 items-center gap-x-1.5">
            <CircleDashed color="gray" />
            Keine Termine verfügbar
          </div>
          {config.dayPicker?.showExtraCostDayInLegend(service) && (
            <div className="flex mb-1 items-center gap-x-1.5">
              <CircleHalfStroke color="yellow" />
              <div className={"flex flex-col"}>
                {t(`booking.daypicker-legend-yellow-${service?.id}`, {
                  defaultValue: "Termine gegen Aufpreis verfügbar",
                })}
                <a href={"#extra-cost-tos"}>Weitere Informationen</a>
              </div>
            </div>
          )}
          <div className="flex mb-1 items-center gap-x-1.5">
            <CircleFull color="green" />
            {t(`booking.daypicker-legend-green-${service?.id}`, {
              defaultValue: "Termine verfügbar",
            })}
          </div>
        </div>
        <SelectedHint
          service={service}
          selectedSlot={selectedSlot}
          garage={garage}
          organization={organization}
        />
      </div>
      <div className="row-span-6 relative">
        <div className="mt-2">
          {safeMap(
            safeFilter(
              eligibleGarageProperties(garage?.garageProperties, availableGarageProperties),
              (property: GarageProperty) => property.show_in_detail_view,
            ),
            (property: GarageProperty) => {
              return (
                <div className="mb-3" key={property.id}>
                  <IconLabel
                    icon={<CheckIcon primary />}
                    text={safeHtmlToReact(property.detail_view_text ?? "")}
                  />
                </div>
              );
            },
          )}
        </div>
        <div className="md:mt-10"></div>
        {service.show_checkpoint && garage.checkpoint && garage.checkpoint.name && (
          <CheckpointDetails
            checkpoint={garage.checkpoint}
            garageType={garage.garageType}
            hideDescription={ServiceIsVehicleReturn(service)}
            countryCode={countryCode}
          />
        )}
        {displayPremiumServices && (
          <PremiumServiceSelector organization={organization} service={service} garage={garage} />
        )}
      </div>

      <div className="flex flex-col sm:flex-row sm:gap-4">
        <button
          type="button"
          onClick={submit}
          disabled={
            loadingWhenBlockingSlot || !daysAvailable || noSubServiceSelected() || !selectedSlot
          }
          className="w-full bg-primary hover:bg-primary-500 px-8 py-2 mt-5 sm:w-72 text-white rounded text-center font-bold font-serif cursor-pointer no-underline disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loadingWhenBlockingSlot && t("booking.blocking")}
          {!loadingWhenBlockingSlot &&
            (service && ServiceIsVehicleReturn(service)
              ? config.dayPicker?.isExtraCostDay(
                  selectedDay,
                  fleetVehicleReturn ? new Date(fleetVehicleReturn.endDate) : null,
                )
                ? "Kostenpflichtig " + t("booking.vehicleReturn.submit")
                : t("booking.vehicleReturn.submit")
              : t("booking.submit"))}
        </button>

        {OrganizationIsSignalIdunaOrItzehoerAndServiceIsGlass(organization, service) && (
          <CallbackRequestButton
            organization={organization}
            garage={garage}
            service={service}
            premiumServices={selectedPremiumServices}
            garageProperties={eligibleGarageProperties(
              garage?.garageProperties ?? null,
              availableGarageProperties,
            )}
          />
        )}
      </div>
    </div>
  );
};
